'use strict';

const {
  finiteNumber,
  roundPct,
  roundMoney,
  computeCompGapAmount,
  computeCompGapPct,
  signDirection,
} = require('./compensationMetrics');

const DEFAULT_PCT_TOLERANCE = 0.6;
const DEFAULT_AMOUNT_TOLERANCE = 2;

/**
 * @param {object} params
 * @returns {{ ok: boolean, issues: string[], recomputed: { gapAmount: number|null, gapPct: number|null }|null }}
 */
function validatePayGapCoherence({
  market,
  pay,
  gapAmount,
  gapPct,
  pctTolerance = DEFAULT_PCT_TOLERANCE,
  amountTolerance = DEFAULT_AMOUNT_TOLERANCE,
} = {}) {
  const issues = [];
  const m = finiteNumber(market);
  const p = finiteNumber(pay);
  const storedAmount = roundMoney(gapAmount);
  const storedPct = roundPct(gapPct);

  if (m == null || p == null || m <= 0) {
    return { ok: storedAmount == null && storedPct == null, issues: ['missing_market_or_pay'], recomputed: null };
  }

  const recomputedAmount = computeCompGapAmount(m, p);
  const recomputedPct = computeCompGapPct(m, p);

  if (storedAmount != null && Math.abs(storedAmount - recomputedAmount) > amountTolerance) {
    issues.push('amount_recompute_mismatch');
  }
  if (storedPct != null && Math.abs(storedPct - recomputedPct) > pctTolerance) {
    issues.push('pct_recompute_mismatch');
  }

  if (storedAmount != null && storedPct != null) {
    const amountDir = signDirection(storedAmount);
    const pctDir = signDirection(storedPct);
    if (amountDir !== 0 && pctDir !== 0 && amountDir !== pctDir) {
      issues.push('sign_mismatch_amount_vs_pct');
    }
  }

  return {
    ok: issues.length === 0,
    issues,
    recomputed: { gapAmount: recomputedAmount, gapPct: recomputedPct },
  };
}

/**
 * Validate base + total comp pairs; recompute inconsistent fields from market/pay.
 * @returns {{ metrics: object, validation: object, anomalies: object[] }}
 */
function validateAndReconcilePayGapBundle({
  salaryAnnual,
  benchmarkAnnual,
  gapAmount,
  gapPct,
  totalCompAnnual,
  benchmarkTotalAnnual,
  totalCompGapAmount,
  totalCompGapPct,
  source = null,
  benchmarkMeta = null,
} = {}) {
  const anomalies = [];
  const baseCheck = validatePayGapCoherence({
    market: benchmarkAnnual,
    pay: salaryAnnual,
    gapAmount,
    gapPct,
  });
  const totalCheck = validatePayGapCoherence({
    market: benchmarkTotalAnnual,
    pay: totalCompAnnual,
    gapAmount: totalCompGapAmount,
    gapPct: totalCompGapPct,
  });

  let nextGapAmount = gapAmount;
  let nextGapPct = gapPct;
  if (!baseCheck.ok && baseCheck.recomputed) {
    anomalies.push({ scope: 'base', issues: baseCheck.issues, action: 'recomputed' });
    nextGapAmount = baseCheck.recomputed.gapAmount;
    nextGapPct = baseCheck.recomputed.gapPct;
  }

  let nextTotalGapAmount = totalCompGapAmount;
  let nextTotalGapPct = totalCompGapPct;
  if (!totalCheck.ok && totalCheck.recomputed) {
    anomalies.push({ scope: 'total', issues: totalCheck.issues, action: 'recomputed' });
    nextTotalGapAmount = totalCheck.recomputed.gapAmount;
    nextTotalGapPct = totalCheck.recomputed.gapPct;
  }

  const usesTotalComp =
    nextTotalGapPct != null &&
    nextTotalGapAmount != null &&
    finiteNumber(benchmarkTotalAnnual) != null &&
    finiteNumber(totalCompAnnual) != null;

  const displayAmount = usesTotalComp ? nextTotalGapAmount : nextGapAmount;
  const displayPct = usesTotalComp ? nextTotalGapPct : nextGapPct;

  const displayCheck = validatePayGapCoherence({
    market: usesTotalComp ? benchmarkTotalAnnual : benchmarkAnnual,
    pay: usesTotalComp ? totalCompAnnual : salaryAnnual,
    gapAmount: displayAmount,
    gapPct: displayPct,
  });

  if (!displayCheck.ok) {
    anomalies.push({ scope: 'display', issues: displayCheck.issues, action: 'blocked' });
  }

  const validation = {
    ok: baseCheck.ok && totalCheck.ok && displayCheck.ok,
    base: baseCheck,
    total: totalCheck,
    display: displayCheck,
    reconciled: anomalies.some((a) => a.action === 'recomputed'),
    source,
    benchmarkMeta,
    validatedAt: new Date().toISOString(),
  };

  return {
    metrics: {
      gapAmount: nextGapAmount,
      gapPct: nextGapPct,
      totalCompGapAmount: nextTotalGapAmount,
      totalCompGapPct: nextTotalGapPct,
      payGapDollars: displayAmount ?? 0,
      payGapPct: displayPct,
      usesTotalComp,
      isUnderMarket: nextGapAmount != null ? nextGapAmount > 0 : null,
      isAtOrAboveMarket: nextGapAmount != null ? nextGapAmount <= 0 : null,
    },
    validation,
    anomalies,
  };
}

/**
 * @param {object} employee
 * @param {object} payMetrics - from computePayGapMetrics
 * @returns {object[]}
 */
function detectCompensationAnomalies(employee, payMetrics = {}) {
  const anomalies = [];
  if (!payMetrics.available) return anomalies;

  const base = validatePayGapCoherence({
    market: payMetrics.benchmarkAnnual,
    pay: payMetrics.salaryAnnual,
    gapAmount: payMetrics.gapAmount,
    gapPct: payMetrics.gapPct,
  });
  if (!base.ok) {
    anomalies.push({
      code: 'BASE_PAY_GAP_INCOHERENT',
      employeeId: employee?.id ?? null,
      issues: base.issues,
    });
  }

  if (payMetrics.usesTotalComp) {
    const total = validatePayGapCoherence({
      market: payMetrics.benchmarkTotalAnnual,
      pay: payMetrics.totalCompAnnual,
      gapAmount: payMetrics.totalCompGapAmount,
      gapPct: payMetrics.totalCompGapPct,
    });
    if (!total.ok) {
      anomalies.push({
        code: 'TOTAL_PAY_GAP_INCOHERENT',
        employeeId: employee?.id ?? null,
        issues: total.issues,
      });
    }

    const baseDir = signDirection(payMetrics.gapAmount);
    const totalDir = signDirection(payMetrics.totalCompGapAmount);
    if (baseDir !== 0 && totalDir !== 0 && baseDir !== totalDir) {
      anomalies.push({
        code: 'BASE_TOTAL_DIRECTION_CONFLICT',
        employeeId: employee?.id ?? null,
        baseGapAmount: payMetrics.gapAmount,
        totalCompGapAmount: payMetrics.totalCompGapAmount,
      });
    }
  }

  const storedMarketSalary = finiteNumber(employee?.marketSalary);
  const benchmarkBase = finiteNumber(payMetrics.benchmarkAnnual);
  if (
    storedMarketSalary != null &&
    benchmarkBase != null &&
    Math.abs(storedMarketSalary - benchmarkBase) > 1
  ) {
    anomalies.push({
      code: 'MARKET_SALARY_DRIFT',
      employeeId: employee?.id ?? null,
      storedMarketSalary,
      benchmarkBase,
    });
  }

  return anomalies;
}

module.exports = {
  DEFAULT_PCT_TOLERANCE,
  DEFAULT_AMOUNT_TOLERANCE,
  validatePayGapCoherence,
  validateAndReconcilePayGapBundle,
  detectCompensationAnomalies,
};
